import React from 'react';
import { NavLink, useLocation, matchPath } from 'react-router-dom';
import { useAuthUnified } from '@/hooks/shared/useAuthUnified';
import { getAllRouteDefinitions, hasAccessToRoute } from '@/domain-registry';
import { LogoWithDomainSwitcher } from '@/shared/components/layout/LogoWithDomainSwitcher';
import {
  ChevronRight,
  ChevronLeft,
  X,
  ChartLine,
  CircleUser,
  CalendarDays,
  UserCheck,
  ChartGantt,
  CircleDollarSign,
  ClipboardList,
  Wallet,
  Gauge,
  Activity
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ForwardRefExoticComponent<any>;
  shortName?: string;
}

interface NavigationGroup {
  label: string;
  items: NavigationItem[];
}

/**
 * HiringSidebar component - Navigation sidebar for hiring domain
 * Responsive design with mobile support
 */
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const { groupAccess } = useAuthUnified();
  const location = useLocation();

  // Gate nav items through the same capability-aware check the router uses, so
  // the sidebar never lists a page the user would be redirected away from.
  // Matching each item to its registered route keeps nav in lockstep with the
  // route's requiredCapability (e.g. hiring:planning) rather than duplicating it.
  const routeDefinitions = getAllRouteDefinitions();
  const canAccessNavItem = (href: string): boolean => {
    if (!groupAccess) return false;
    const route = routeDefinitions.find(r => matchPath(r.path, href));
    return route ? hasAccessToRoute(route) : true;
  };

  // Navigation grouped by intent: Operational is the day-to-day hiring work
  // (who we have, when they land, where they sit); Strategic is the planning
  // and money layer (forecast, rates, envelopes, rollup).
  const getNavigationGroups = (): NavigationGroup[] => {
    const groups: NavigationGroup[] = [
      {
        label: 'Operational',
        items: [
          { name: 'Headcount', href: '/hiring/headcount', icon: CircleUser, shortName: 'Headcount' },
          { name: 'Timing', href: '/hiring/timing', icon: CalendarDays, shortName: 'Timing' },
          { name: 'Placement', href: '/hiring/placement', icon: UserCheck, shortName: 'Placement' },
          { name: 'Calendar', href: '/hiring/calendar', icon: ChartGantt, shortName: 'Calendar' },
          { name: 'Position Planning', href: '/hiring/positions', icon: ClipboardList, shortName: 'Position Planning' },
        ],
      },
      {
        label: 'Strategic',
        items: [
          { name: 'Forecast', href: '/hiring/forecast', icon: ChartLine, shortName: 'Forecast' },
          { name: 'Rate Card', href: '/hiring/rate-card', icon: CircleDollarSign, shortName: 'Rate Card' },
          { name: 'Envelopes', href: '/hiring/envelopes', icon: Wallet, shortName: 'Envelopes' },
          { name: 'Rollup', href: '/hiring/rollup', icon: Gauge, shortName: 'Rollup' },
          { name: 'Health', href: '/hiring/health', icon: Activity, shortName: 'Health' },
        ],
      },
    ];

    return groups
      .map(group => ({
        ...group,
        items: group.items.filter(item => canAccessNavItem(item.href)),
      }))
      .filter(group => group.items.length > 0);
  };

  const navigationGroups = getNavigationGroups();

  const isActiveRoute = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - Floating Container */}
      <div
        className={`fixed z-50 bg-white transform transition-all duration-300 ease-in-out
          lg:left-4 lg:top-4 lg:bottom-4 lg:rounded-2xl lg:shadow-2xl lg:translate-x-0
          inset-y-0 left-0 shadow-lg
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-20' : 'w-72'}
        `}
      >
        {/* Collapse toggle button - Desktop only, positioned outside overflow container */}
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex absolute -right-3 top-[68px] z-[60] w-6 h-6 items-center justify-center bg-white rounded-full border border-gray-200 shadow-md hover:shadow-lg hover:border-gray-300 hover:scale-110 transition-all cursor-pointer"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          type="button"
        >
          {isCollapsed ? (
            <ChevronRight className="h-3.5 w-3.5 text-gray-600" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5 text-gray-600" />
          )}
        </button>

        <div className="flex flex-col h-full lg:rounded-2xl overflow-hidden">
          {/* Sidebar header - Mobile close button only */}
          <div className="flex items-center justify-end p-4 border-b border-gray-200 lg:hidden">
            <button
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Logo with integrated Domain Switcher */}
          <LogoWithDomainSwitcher isCollapsed={isCollapsed} />

          {/* Navigation */}
          <nav className="flex-1 px-4 py-5 overflow-y-auto">
            {navigationGroups.map((group, groupIndex) => (
              <div key={group.label} className={groupIndex > 0 ? 'mt-6' : ''}>
                {isCollapsed ? (
                  groupIndex > 0 ? <div className="mx-3 mb-3 border-t border-gray-200" /> : null
                ) : (
                  <p className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {group.label}
                  </p>
                )}
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = isActiveRoute(item.href);

                    return (
                      <NavLink
                        key={item.name}
                        to={item.href}
                        onClick={() => {
                          // Close mobile menu when navigating
                          if (window.innerWidth < 1024) {
                            onClose();
                          }
                        }}
                        className={`group flex items-center text-sm font-medium rounded-lg transition-all ${
                          isCollapsed ? 'px-3 py-3 justify-center' : 'px-3 py-2'
                        } ${
                          isActive
                            ? 'bg-gradient-to-r from-[#5b3d72] to-[#704a8b] text-white shadow-md'
                            : 'text-gray-600 hover:bg-[#ebe4f1]/50 hover:text-[#311c3b]'
                        }`}
                        title={item.name} // Show full name on hover when collapsed
                      >
                        <Icon
                          className={`h-5 w-5 transition-colors ${
                            isCollapsed ? '' : 'mr-3'
                          } ${
                            isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#311c3b]'
                          }`}
                        />
                        {!isCollapsed && (
                          <span className="flex-1">
                            {item.shortName || item.name}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

        </div>
      </div>
    </>
  );
};

export default Sidebar;